import { gql, useQuery, useSubscription } from "@apollo/client";
import { newTransactionSubscription } from "../../Admin/Transaction/FindTransactions";
import { 
  CustomerTransactionsQueryQuery, 
  CustomerTransactionsQueryQueryVariables, 
  Transaction 
} from "../../../schemaTypes";
import { useCallback } from "react";

export const customerTransactionsQuery = gql`
  query CustomerTransactionsQuery(
    $offset: Int!
    $limit: Int!
    $duration: String
    $filterBy: String
    $typeFilter: String
    $startDate: String
    $endDate: String
    $searchText: String
  ){
    customerTransactions(
      offset: $offset
      limit: $limit
      duration: $duration
      filterBy: $filterBy
      typeFilter: $typeFilter
      startDate: $startDate
      endDate: $endDate
      searchText: $searchText
    ){
      createdAt
      transactions {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        wallet {
          id
          balance
          currency
          createdAt
          updatedAt
          barterId
          flAccountReference
          owner {
            id
            customer {
              id
              firstname
              lastname
              avatarUrl
            }
            # createdAt
            # updatedAt
          }
        }
        recipientWallet {
          id
          balance
          currency
          createdAt
          updatedAt
          barterId
          flAccountReference
          owner {
            id
            customer {
              id
              firstname
              lastname
              avatarUrl
            }
            # createdAt
            # updatedAt
            admin{
              id
              merchantProfile{
                id
                companyName
                logoUrl
              }
            }
          }
        }
        offer{
          id
          title
          description
          bannerUrl
          category {
            id
            name
            imgSrc
            createdBy{
              id
              role
            }
            createdAt
            updatedAt
          }
          offerType
          discountType
          discountValue
          partnerVoucher
          referralUrl
          status
          usageLimitPerUser
          totalUsageLimit
          startDate
          endDate
          community{
            id
            name
            alias
          }
          redemptions
          revenueGenerated
          clickThroughs
          featured
          createdBy{
            id
            role
            admin {
              id
              shortId
              firstname
              lastname
              email
              phone
              merchantProfile {
                id
                businessName
                businessType
                regCountry
                businessRegNumber
                taxId
                address
                addressAlt
                city
                country
                postcode
                companyName
                username
                industry {
                  id
                  name
                  createdBy{
                    id
                    role
                  }
                  createdAt
                  updatedAt
                }
                companyLocation
                logoUrl
                bannerUrl
                description
                commissionRate
                createdAt
                updatedAt
              }
              createdAt
              updatedAt
            }
          }
          # njoyCode {
          #   id
          #   code
          #   redeemedAt
          #   createdAt
          #   updatedAt
          # }
          createdAt
          updatedAt
        }
        user {
          id
          # createdAt
          # updatedAt
        }
        njoyCode
        partnerVoucher
        point
        createdAt
        updatedAt
      }
    }
  }
`;

newTransactionSubscription;

export interface WithCustomerTransactions {
  myTransactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
}

interface Props {
  variables: CustomerTransactionsQueryQueryVariables;
  children: (data: WithCustomerTransactions) => React.ReactElement | null;
}

export const CustomerTransactions: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore
    // , subscribeToMore 
  } = useQuery<
    CustomerTransactionsQueryQuery,
    CustomerTransactionsQueryQueryVariables
  >(customerTransactionsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Subscription for new transactions
  useSubscription(newTransactionSubscription, {
    onSubscriptionData: ({ client, subscriptionData }) => {
      if (!subscriptionData.data) return;
      client.cache.modify({
        fields: {
          myTransactions(existing = []) {
            return [...existing, subscriptionData.data.newTransaction];
          }
        }
      });
    }
  });

 let myTransactions: Transaction[] = [];

  if (data?.customerTransactions) {
    myTransactions = data.customerTransactions.reduce(
      (acc, group) => {
        if (group?.transactions) {
          return acc.concat(group.transactions as Transaction[]);
        }
        return acc;
      },
      [] as Transaction[]
    );
}

  let hasMoreTransactions = myTransactions.length % variables.limit === 0;
  if (myTransactions.length <= variables.offset) {
    hasMoreTransactions = false;
  }

  const loadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        offset: myTransactions.length
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (
          !fetchMoreResult ||
          !fetchMoreResult.customerTransactions ||
          !prev ||
          !prev.customerTransactions
        ) {
          return prev;
        }
        return {
          ...prev,
          myTransactions: [
            ...prev.customerTransactions,
            ...fetchMoreResult.customerTransactions
          ]
        };
      }
    });
  }, [fetchMore, variables, myTransactions.length]);

  return children({
    hasMoreTransactions,
    myTransactions,
    loading,
    loadMore,
  });
};